export function setOptionLabel(option, getOptionLabel) {
    if (option) {
        if (typeof option === 'object') {
            return getOptionLabel ? getOptionLabel(option) : option;
        } else {
            return option;
        }
    }

    return '';
}

/**
 * Which option fields participate in search.
 *
 * This is an allowlist rather than a walk over every value, because indexing
 * everything made short queries useless: personnel options carry
 * `accessType: "PERMANENT"`, so typing "a" (or e, n, r, t, m, p, o, i, l, u, d,
 * s, b, k, g) matched 100% of rows on a field the user cannot see. Matching only
 * what the option actually renders also means every hit is explainable — the
 * matched text is highlighted rather than invisibly matched on an id or a colour.
 *
 * Seeded from the components that display option text: Option/AdditionalInfo,
 * Option/InventoryOption, Option/TrailerOption, Option/VehicleOption,
 * Option/EquipmentOption/* and UI/Alerts/Alert/Note.
 *
 * The option's primary label (getOptionLabel/displayExpr) is indexed separately
 * in `primaryIndex`, so it does not need to be listed here — including fields
 * whose key is a dynamic dataField such as `trailer.trailerNumber`.
 */
export const SEARCHABLE_SCALAR_FIELDS = [
    // person
    'description',
    'fullName',
    'firstName',
    'lastName',
    'name',
    'identificationNumber',
    'dl',
    'cdl',
    'position',
    'dateOfBirth',
    'companyCardNumber',
    'address',
    'phoneNumber',
    'industryType',
    'relationType',
    // vehicle / trailer / equipment
    'unitNumber',
    'licensePlateNumber',
    'licencePlate',
    'vinNumber',
    'equipmentNumber',
    'trailerNumber',
    'containerNumber',
    'chassisNumber',
    'dollyNumber',
    'equipmentStatus',
    'inventoryStatus',
    // generic value/label lookups (enums, waiting reasons, canned notes)
    'label',
    'value',
];

export const SEARCHABLE_NESTED_FIELDS = {
    company: ['fullName', 'name'],
    equipmentCategory: ['name'],
    inventoryItemType: ['name'],
    gate: ['name'],
};

export const SEARCHABLE_LIST_FIELDS = {
    alerts: ['name', 'note'],
    equipmentAlerts: ['name', 'note'],
    personnelList: ['fullName'],
    gateList: ['name'],
};

function pushSearchableValue(value, acc) {
    if (value === null || value === undefined || value === '') return;
    const type = typeof value;
    if (type === 'string') {
        acc.push(value.toLowerCase());
    } else if (type === 'number' || type === 'boolean') {
        acc.push(String(value).toLowerCase());
    }
}

/**
 * Collects the searchable string values of an option into a single flat array.
 * This is done ONCE per option (see buildOptionSearchIndex) rather than on every
 * keystroke, avoiding a deep, per-keystroke recursion over every field.
 */
function collectSearchableStrings(option, acc) {
    if (option === null || option === undefined) return acc;

    // Primitive option lists (freeSolo string/number lookups) index as-is.
    if (typeof option !== 'object') {
        pushSearchableValue(option, acc);
        return acc;
    }

    for (const key of SEARCHABLE_SCALAR_FIELDS) {
        pushSearchableValue(option[key], acc);
    }

    for (const key of Object.keys(SEARCHABLE_NESTED_FIELDS)) {
        const nested = option[key];
        if (!nested || typeof nested !== 'object') continue;
        for (const field of SEARCHABLE_NESTED_FIELDS[key]) {
            pushSearchableValue(nested[field], acc);
        }
    }

    for (const key of Object.keys(SEARCHABLE_LIST_FIELDS)) {
        const list = option[key];
        if (!Array.isArray(list)) continue;
        for (const item of list) {
            if (!item || typeof item !== 'object') {
                pushSearchableValue(item, acc);
                continue;
            }
            for (const field of SEARCHABLE_LIST_FIELDS[key]) {
                pushSearchableValue(item[field], acc);
            }
        }
    }

    return acc;
}

/**
 * Precomputes, once per option list change:
 *  - searchIndex: the lowercased searchable strings for every option (deep walk).
 *  - primaryIndex: the lowercased primary label (from getOptionLabel) for every
 *    option, so per-keystroke filtering doesn't re-call getOptionLabel + toString
 *    + toLowerCase for every option on every keystroke.
 *
 * Both are WeakMaps keyed by option reference so lookups during filtering are
 * O(1) and don't re-walk/re-lowercase the object tree or re-derive the label.
 */
export function buildOptionSearchIndex(options, getOptionLabel) {
    const searchIndex = new WeakMap();
    const primaryIndex = new WeakMap();
    if (!Array.isArray(options)) return { searchIndex, primaryIndex };
    for (const option of options) {
        if (option && typeof option === 'object') {
            searchIndex.set(option, collectSearchableStrings(option, []));
            if (getOptionLabel) {
                const primaryValue = getOptionLabel(option);
                primaryIndex.set(option, primaryValue != null ? primaryValue.toString().toLowerCase() : '');
            }
        }
    }
    return { searchIndex, primaryIndex };
}

/**
 * Returns the precomputed (lowercased) primary label for an option. Falls back
 * to deriving it on demand (should be rare once the index is built), keeping the
 * exact same result the previous inline getOptionLabel() call produced.
 */
export function getPrimaryLabel(option, primaryIndex, getOptionLabel) {
    if (primaryIndex && option && typeof option === 'object') {
        const cached = primaryIndex.get(option);
        if (cached !== undefined) return cached;
    }
    if (!getOptionLabel) return '';
    const primaryValue = getOptionLabel(option);
    return primaryValue != null ? primaryValue.toString().toLowerCase() : '';
}

function getSearchStrings(option, searchIndex) {
    if (typeof option === 'string') return [option.toLowerCase()];
    if (searchIndex && option && typeof option === 'object') {
        const cached = searchIndex.get(option);
        if (cached) return cached;
    }
    // Fallback: compute on demand (should be rare once the index is built).
    return collectSearchableStrings(option, []);
}

export function checkIfValueIncludes(option, inputTextStandardized, searchIndex) {
    const strings = getSearchStrings(option, searchIndex);
    for (let i = 0; i < strings.length; i++) {
        if (strings[i].includes(inputTextStandardized)) return true;
    }
    return false;
}

export function checkIfValueStartsWith(option, inputTextStandardized, searchIndex) {
    const strings = getSearchStrings(option, searchIndex);
    for (let i = 0; i < strings.length; i++) {
        if (strings[i].startsWith(inputTextStandardized)) return true;
    }
    return false;
}
